import React, { useEffect } from "react";
import { POS_COLORS } from "../data/players";
import { MOCK_ROSTER_SIZE } from "../data/archetypes";
import { BYE_WEEKS_2026 } from "../data/scheduleData";
import { fillRosterSlots } from "../utils/rosterSlotFill";
import { usePlayerCard } from "../context/PlayerCardContext";
import theme from "../data/theme";

const stripSuffix = (name) => name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim();

function toInitialLastName(name) {
  const clean = stripSuffix(name);
  const parts = clean.split(" ");
  if (parts.length < 2) return clean;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
}

export default function ViewAllRostersModal({ isOpen, onClose, teams, userSlot, rosterSlots }) {
  const { openPlayerCard } = usePlayerCard();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const teamList = Array.isArray(teams) ? teams : Object.values(teams || {});
  // Match Max to the slot rows actually rendered below (settings-derived), not
  // the stale 16-man MOCK_ROSTER_SIZE constant.
  const rosterSize = rosterSlots?.length || MOCK_ROSTER_SIZE;
  const teamCount = teamList.length;
  const columns = Math.ceil(teamCount / 2);
  const modalMaxWidth = Math.min(columns * 180 + 80, 1600);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: modalMaxWidth, width: "95%", maxHeight: "90vh", padding: 20, display: "flex", flexDirection: "column" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexShrink: 0 }}>
          <h3 style={{ fontFamily: "inherit", fontWeight: 700, fontSize: 18, margin: 0, color: theme.colors.brand.orange }}>
            All Rosters
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", color: theme.colors.text.secondary, fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 4, fontFamily: "inherit" }}
          >
            ×
          </button>
        </div>

        <div
          className="view-all-rosters-grid"
          style={{ "--cols": columns, gap: 8, flex: 1, overflowY: "auto", overflowX: "auto", minHeight: 0 }}
        >
          {teamList.map((team, i) => {
            if (!team) return null;
            const isUser = !!team.isUser;
            const filled = fillRosterSlots(team.roster, rosterSlots);
            const emptySlots = rosterSize - team.roster.length;
            const maxBid = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);

            return (
              <div
                key={i}
                style={{
                  background: theme.colors.bg.surface,
                  border: `1px solid ${isUser ? theme.colors.border.brand : theme.colors.border.subtle}`,
                  borderRadius: 8,
                  padding: "8px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: isUser ? theme.colors.brand.orange : theme.colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {team.name || `Team ${i + 1}`}{isUser ? " (You)" : ""}
                  </div>
                  {!isUser && team.profile?.archetype && (
                    <div style={{ fontSize: 9, color: theme.colors.text.secondary, marginTop: 1 }}>{team.profile.archetype}</div>
                  )}
                  <div style={{ fontSize: 10, color: theme.colors.text.secondary, marginTop: 2 }}>
                    <span style={{ fontWeight: 700, color: team.budget < 20 ? theme.colors.status.danger : theme.colors.extended.emerald }}>${team.budget}</span>
                    {" · Max "}
                    <span style={{ fontWeight: 700, color: theme.colors.brand.accent }}>${maxBid}</span>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {rosterSlots.map(slot => {
                    const pick = filled[slot.id];
                    const slotPos = slot.pos === "BN" ? "BN" : slot.pos === "FLEX" ? "FLEX" : slot.pos === "SFLEX" ? "SFLEX" : pick?.player.pos || slot.pos;

                    return (
                      <div
                        key={slot.id}
                        style={{ display: "flex", alignItems: "center", gap: 5, padding: "2px 0", opacity: pick ? 1 : 0.4 }}
                      >
                        <span
                          className="tag"
                          style={{
                            background: POS_COLORS[slotPos]?.bg || theme.colors.bg.elevated,
                            color: POS_COLORS[slotPos]?.text || theme.colors.text.muted,
                            fontSize: 8, padding: "1px 4px", minWidth: 32, textAlign: "center", fontWeight: 700,
                          }}
                        >
                          {slot.label}
                        </span>
                        {pick ? (
                          <>
                            <span
                              className="vr-player-name"
                              onClick={() => openPlayerCard(pick.player.name, {}, "rosters_modal")}
                              style={{ flex: 1, fontSize: 10, fontWeight: 500, color: theme.colors.text.primary, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                              title={pick.player.name}
                            >
                              {toInitialLastName(pick.player.name)}
                            </span>
                            {BYE_WEEKS_2026[pick.player.team] && <span style={{ fontSize: 9, color: theme.colors.text.muted }} title="Bye week">B{BYE_WEEKS_2026[pick.player.team]}</span>}
                            <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.brand.accent }}>${pick.price}</span>
                          </>
                        ) : (
                          <span style={{ flex: 1, fontSize: 10, color: theme.colors.border.default, fontStyle: "italic" }}>Empty</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 16, flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{
              background: "linear-gradient(135deg, #FF6B00, #E55A00)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              width: 200,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: "0.5px",
            }}
          >
            Close & Resume
          </button>
        </div>
      </div>
    </div>
  );
}
